'use client';

import { useSyncExternalStore } from 'react';
import { Box, Link } from '@/primitives';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import {
  getInbreedingInfoDismissedSnapshot,
  markInbreedingInfoDismissed,
  subscribeInbreedingInfoDismissed,
} from './inbreedingInfoDismissed';

interface InbreedingInfoAlertProps {
  /** Opens the detailed inbreeding-coefficient explanation modal. */
  onLearnMore: () => void;
}

/**
 * Dismissible explainer (session-only) noting that BreedLOGIC's inbreeding
 * figures may differ slightly from Internet Solutions / ILROnline. Renders
 * nothing once dismissed for the current tab.
 */
export default function InbreedingInfoAlert({
  onLearnMore,
}: InbreedingInfoAlertProps) {
  const dismissed = useSyncExternalStore(
    subscribeInbreedingInfoDismissed,
    getInbreedingInfoDismissedSnapshot,
    () => false,
  );

  if (dismissed) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: { xs: 16, md: 24 },
        left: { xs: 16, md: 24 },
        right: { xs: 16, md: 24 },
        zIndex: (theme) => theme.zIndex.snackbar,
      }}
    >
      <Alert
        severity="info"
        variant="outlined"
        sx={{
          py: 0.5,
          boxShadow: 2,
          bgcolor: 'background.paper',
        }}
        action={
          <IconButton
            size="small"
            aria-label="Dismiss"
            onClick={markInbreedingInfoDismissed}
            sx={{ alignSelf: 'center' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      >
        BreedLOGIC uses the same inbreeding calculation as Internet Solutions
        and ILROnline but has access to more complete ancestry data. As a
        result, inbreeding coefficients and pedigree depth statistics will be
        more accurate and may differ slightly.{' '}
        <Link
          component="button"
          type="button"
          onClick={onLearnMore}
          sx={{ verticalAlign: 'baseline', fontSize: 'inherit' }}
        >
          Learn more
        </Link>
      </Alert>
    </Box>
  );
}
